import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react'

import Modal from '@Modal/V3/Modal'

import FormStatus from '@/Form/FormStatus'

import Message from '@Pure/Message'
import Loader  from '@Pure/Loader'

import request from '@function/ajax/request'
import bemit   from '@function/bemit'

import { formatNum, getRenewalYear, num } from '@root/utility'

import DashboardStore from '../Stores/Dashboard'

const { app } = window;

const DASHBOARD = 'PartnersList';

// eslint-disable-next-line react/no-multi-comp
export default function SetPartnersRenewalModal ({ ...props })
{
	const [status, setStatus] = useState({ });
	const [loading, setLoading] = useState(false);
	const [partners, setPartners] = useState([]);

	const ids = DashboardStore.useState(function (store)
	{
		if (!store[DASHBOARD]?.cbs) return { };

		return Object.keys(store[DASHBOARD].cbs);
	});

	const _ref_busy = useRef();

	const _refs = useRef();

	useEffect(function ()
	{
		_refs.current ||= { };

		_refs.current.onSuccess = props.onSuccess;
		_refs.current.onClose = props.onClose;
	},
	[props.onSuccess, props.onClose]);

// réinitialise la modale une fois masquée pour éviter
// de modifier ce qui est affiché en pleine animation d'ouverture/fermeture
	const handleModalHidden = useCallback(function (params)
	{
		setStatus({ });
		setPartners([]);
	},
	[]);

	const modifiers = useMemo(function ()
	{
		return props.bemit || ['small'];
	},
	[props.bemit]);

	useEffect(function ()
	{
		if (!props.open) return () => void 0;

		setLoading('fetching');

		(async function fetchPartners ()
		{
			try
			{
				var { partners } = await request('/partners-renewals/list-from-ids')
				.config({ })
				.post({ ids: ids });
			}
			catch (update)
			{
				return setStatus(update);
			}
			finally
			{
				_ref_busy.current = false;
				setLoading(false);
			}

			setPartners(partners || []);
		})();
	},
	[props.open, ids]);

	const validate = useCallback(function ()
	{
		setLoading('updating');

		setStatus({ });

		(async function update ()
		{
			try
			{
				var response = await request('/partners-renewals/set-renewal')
				.config({ })
				.post({ ids: partners.map(p => p.id) });
			}
			catch (update)
			{
				return setStatus(update);
			}
			finally
			{
				_ref_busy.current = false;
				setLoading(false);
			}

			app.emit('updatePartnersList', { source: 'SetPartnersRenewalModal' });

			setStatus(response);

			if (typeof _refs.current?.onSuccess === 'function')
			{
				_refs.current.onSuccess(response);
			}

		// on décoche tout
			DashboardStore.update(function (store)
			{
				store[DASHBOARD].cbs = { };
			});

			_refs.current?.onClose?.();
		})();

		return () => { _ref_busy.current = false; };
	},
	[partners]);

	const prevent_close = loading === 'updating';

	const content = partners && partners.length > 0 ?
	(
		<Message color="orange">
			Vous allez marquer comme renouvelé pour l'année
			{ ' ' }
			<strong>
				{ num(getRenewalYear()) + 1 }
			</strong>
			{ ' ' }
			{ partners.length > 1 ? 'les' : 'la' }
			{ partners.length > 1 ? ' ' : undefined }
			{ partners.length > 1 ? formatNum(partners.length) : undefined }
			{ ' ' }
			fiche
			{ partners.length > 1 ? 's' : '' }
			{ ' ' }
			partenaire ci-dessous.
		</Message>
	)
	: undefined;

	const footer =
	(
		<>
			<FormStatus { ...status } />

			<button type="button" aria-label="Fermer"
				className={ bemit('c-btn', ['green']) }
				onClick={ validate } disabled={ !!loading }>
				Mettre à jour
			</button>

			<button type="button" aria-label="Fermer"
				className={ props['close-classes'] || bemit('c-btn', ['red']) }
				onClick={ props.onClose }>
				Fermer
			</button>

			{
				loading === 'updating' ?
				(
					<Loader className="c-loader" bemit={ ['primary', 'bg-white'] }
						label="Mise à jour..."
					/>
				)
				: undefined
			}

		</>
	);

	const list_items = (partners || []).map(function ({ id, name, city })
	{
		return (
			<div className="c-dashboard__row" key={ id }>
				<div className="c-dashboard__cell Partner-name">
					{ name }
				</div>

				<div className="c-dashboard__cell Partner-city">
					{ city }
				</div>
			</div>
		);
	});

	const list = partners && partners.length > 0 ?
	(
		<>
			<div className={ bemit('c-dashboard', ['head', 'partner', 'in-modal']) }>

				<div className="c-dashboard__row">
					<div className="c-dashboard__cell Partner-name">
						Nom
					</div>

					<div className="c-dashboard__cell Partner-city">
						Adresse
					</div>
				</div>

			</div>

			<div className={ bemit('c-dashboard', ['partner', 'in-modal']) }>
				{ list_items }
			</div>
		</>
	)
	: undefined;

	return (
		<Modal open={ props.open === true } onClose={ props.onClose }
			bemit={ modifiers } trg={ props.trg } footer={ footer }
			title="Mise à jour des renouvellements"
			prevent-close={ prevent_close }
			afterHidden={ handleModalHidden }>

			{ content }

			{
				loading === 'fetching' ?
				(
					<Loader className="c-loader" bemit={ ['primary', 'bg-white'] }
						label="Chargement des partenaires..."
					/>
				)
				: list
			}

			<div className="u-margin-top-5" />

		</Modal>
	);
}